import { useMemo, useState } from 'react';
import {
  Modal, ModalPortal, ModalOverlay, ModalContent, ModalHeader, ModalTitle, ModalDescription,
  Input, Textarea, Button,
} from '@klaro7/ui';
import { toast } from 'sonner';

import { resolveUploadUrl } from '@/lib/uploads';
import { useAdminProfessionals } from '@/features/professionals/hooks/useAdminProfessionals';
import { extractErrorMessage } from '@/lib/api';
import { useBroadcastSupport } from '../hooks/useSupport';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_CHARS = 5000;

/**
 * Bulk staff communication: pick many consultants (or all of them) and send the
 * same support message to each in one shot. "Tutti i consulenti" is resolved
 * server-side so it truly reaches everyone, regardless of the loaded page.
 */
export function BroadcastSupportModal({ open, onOpenChange }: Props) {
  const [search, setSearch] = useState('');
  const [content, setContent] = useState('');
  const [allMode, setAllMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useAdminProfessionals({ limit: 100 });
  const broadcast = useBroadcastSupport();

  const professionals = useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.pagination?.total ?? professionals.length;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? professionals.filter((p) => {
        const name = `${p.userId?.profile?.firstName ?? ''} ${p.userId?.profile?.lastName ?? ''}`.toLowerCase();
        return name.includes(q) || (p.title ?? '').toLowerCase().includes(q);
      })
    : professionals;

  const reset = () => {
    setSearch('');
    setContent('');
    setAllMode(false);
    setSelected(new Set());
  };

  const close = () => {
    onOpenChange(false);
    reset();
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // "Seleziona tutti (visibili)": adds/removes every currently filtered row from
  // the explicit selection. Distinct from the "Tutti i consulenti" master toggle.
  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((p) => next.delete(p.id));
      else filtered.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const recipientCount = allMode ? totalCount : selected.size;
  const trimmed = content.trim();
  const canSend =
    !!trimmed && trimmed.length <= MAX_CHARS && recipientCount > 0 && !broadcast.isPending;

  const submit = () => {
    if (!canSend) return;
    broadcast.mutate(
      allMode
        ? { content: trimmed, allProfessionals: true }
        : { content: trimmed, professionalIds: Array.from(selected) },
      {
        onSuccess: (result) => {
          if (result.failed === 0) {
            toast.success(`Comunicazione inviata a ${result.sent} consulenti`);
          } else {
            toast.warning(
              `Inviata a ${result.sent} su ${result.total} consulenti (${result.failed} non riusciti)`
            );
          }
          close();
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      }
    );
  };

  return (
    <Modal open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <ModalPortal>
        <ModalOverlay />
        <ModalContent className="max-w-lg">
          <ModalHeader>
            <ModalTitle>Comunicazione ai consulenti</ModalTitle>
            <ModalDescription>
              Invia lo stesso messaggio a più consulenti (o a tutti) in una volta sola.
            </ModalDescription>
          </ModalHeader>

          <div className="mt-3 space-y-3">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Scrivi la comunicazione…"
              rows={4}
              maxLength={MAX_CHARS}
              autoFocus
            />
            <p className="text-right text-[11px] text-neutral-400">{content.length}/{MAX_CHARS}</p>

            {/* Master toggle: send to the whole roster, server-resolved. */}
            <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5">
              <input
                type="checkbox"
                checked={allMode}
                onChange={(e) => setAllMode(e.target.checked)}
                className="h-4 w-4 shrink-0 rounded border-neutral-300 text-brand-orange focus:ring-brand-orange"
              />
              <span className="text-[13px] font-semibold text-neutral-900">
                Invia a tutti i consulenti
                <span className="ml-1 font-normal text-neutral-500">({totalCount})</span>
              </span>
            </label>

            {!allMode && (
              <>
                <Input
                  placeholder="Cerca per nome o titolo…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <div className="flex items-center justify-between px-1">
                  <label className="flex cursor-pointer items-center gap-2 text-[12px] font-medium text-neutral-600">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                    />
                    Seleziona tutti {q ? '(filtrati)' : '(visibili)'}
                  </label>
                  <span className="text-[11px] text-neutral-400">{selected.size} selezionati</span>
                </div>

                <div className="max-h-64 overflow-y-auto rounded-xl border border-neutral-200">
                  {isLoading ? (
                    <p className="p-4 text-sm text-neutral-400">Caricamento…</p>
                  ) : filtered.length === 0 ? (
                    <p className="p-4 text-sm text-neutral-400">Nessun professionista trovato</p>
                  ) : (
                    filtered.map((p) => {
                      const name =
                        `${p.userId?.profile?.firstName ?? ''} ${p.userId?.profile?.lastName ?? ''}`.trim() ||
                        'Professionista';
                      const checked = selected.has(p.id);
                      return (
                        <label
                          key={p.id}
                          className="flex w-full cursor-pointer items-center gap-3 border-b border-neutral-100 px-4 py-2.5 text-left last:border-b-0 hover:bg-neutral-50"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(p.id)}
                            className="h-4 w-4 shrink-0 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
                          />
                          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full bg-neutral-100">
                            {p.userId?.profile?.avatar && (
                              <img
                                src={resolveUploadUrl(p.userId.profile.avatar)}
                                alt=""
                                className="h-8 w-8 object-cover"
                              />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13px] font-semibold text-neutral-900">{name}</p>
                            {p.title && <p className="truncate text-[11px] text-neutral-400">{p.title}</p>}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-[12px] text-neutral-500">
              {recipientCount > 0
                ? `${recipientCount} destinatar${recipientCount === 1 ? 'io' : 'i'}`
                : 'Nessun destinatario'}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={close} disabled={broadcast.isPending}>
                Annulla
              </Button>
              <Button
                type="button"
                onClick={submit}
                disabled={!canSend}
                className="bg-brand-orange text-white hover:bg-brand-orange/90"
              >
                {broadcast.isPending ? 'Invio…' : 'Invia'}
              </Button>
            </div>
          </div>
        </ModalContent>
      </ModalPortal>
    </Modal>
  );
}
